import { io } from 'socket.io-client'
import { getUserData } from '@src/utility/Utils'

class WebSocketService {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 1000
    this.listeners = new Map()
  }

  getAccessToken() {
    const userData = getUserData()
    return userData?.accessToken || null
  }

  /**
   * Conecta ao servidor WebSocket
   * @param {string} url - URL do servidor WebSocket (padrão: ws://localhost:3001)
   */
  connect(url = 'wss://wsbds.stg.theripen.com') {
    try {
      // Desconecta se já houver uma conexão ativa
      if (this.socket) {
        this.disconnect()
      }

      const accessToken = this.getAccessToken()
      if (!accessToken) {
        console.warn('⚠️ Token JWT ausente. Conexão WebSocket cancelada.')
        return
      }

      // Cria nova conexão
      this.socket = io(url, {
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: true,
        auth: { token: accessToken }
      })

      this.setupEventListeners()
      
      console.log('🔌 Tentando conectar ao WebSocket:', url)
      
    } catch (error) {
      console.error('❌ Erro ao conectar WebSocket:', error)
    }
  }

  /**
   * Configura os listeners básicos do socket
   */
  setupEventListeners() {
    if (!this.socket) return

    // Evento de conexão bem-sucedida
    this.socket.on('connect', () => {
      console.log('✅ WebSocket conectado com sucesso')
      this.isConnected = true
      this.reconnectAttempts = 0
      
      // Entra na sala do usuário automaticamente
      this.joinUserRoom()
    })

    // Evento de desconexão
    this.socket.on('disconnect', (reason) => {
      console.log('🔌 WebSocket desconectado:', reason)
      this.isConnected = false
      
      // Tenta reconectar automaticamente se não foi desconexão manual
      if (reason !== 'io client disconnect') {
        this.handleReconnect()
      }
    })

    // Evento de erro de conexão
    this.socket.on('connect_error', (error) => {
      console.error('❌ Erro de conexão WebSocket:', error)
      this.isConnected = false
      this.handleReconnect()
    })

    // Listener para notificações
    this.socket.on('notification', (data) => {
      console.log('🔔 Notificação recebida:', data)
      this.handleNotification(data)
    })
  }

  /**
   * Entra na sala do usuário usando o email como identificador
   */
  joinUserRoom(userId = null) {
    const userData = getUserData()
    
    if (userData && this.socket) {
      if (userId) {
        this.socket.emit('join-user', { userId })
        console.log('🏠 Entrando em sala de usuário customizada:', userId)
        return
      }

      // Compatibilidade com o fluxo atual da aplicação
      this.socket.emit('join-user', {})
      
      console.log('🏠 Solicitando entrada na sala de usuário autenticado')
    } else {
      console.warn('⚠️ Dados do usuário não encontrados para entrar na sala')
    }
  }

  /**
   * Entra em uma sala de caso específico
   * @param {string} caseId - ID do caso
   */
  joinCaseRoom(caseId) {
    if (this.socket && caseId) {
      this.socket.emit('join-case', { caseId })
      console.log('🏠 Entrando na sala do caso:', caseId)
    }
  }

  /**
   * Trata as notificações recebidas
   * @param {Object} notification - Dados da notificação
   */
  async handleNotification(notification) {
    console.log('🔔 Notificação recebida no serviço:', notification)
    
    // Exibe notificação nativa do navegador
    this.showBrowserNotification(notification)
    
    // Atualiza contador de notificações não lidas
    await this.updateUnreadCount()
    
    // Chama todos os listeners registrados para notificações
    const notificationListeners = this.listeners.get('notification') || []
    console.log('📢 Chamando', notificationListeners.length, 'listeners de notificação')
    
    notificationListeners.forEach((callback, index) => {
      try {
        console.log('📞 Chamando listener', index + 1)
        callback(notification)
      } catch (error) {
        console.error('❌ Erro ao processar notificação no listener', index + 1, ':', error)
      }
    })
  }

  /**
   * Atualiza o contador de notificações não lidas
   */
  async updateUnreadCount() {
    try {
      // Importa dinamicamente para evitar dependência circular
      const { notificationService } = await import('./api.js')
      
      console.log('📊 Buscando contador de notificações não lidas...')
      const response = await notificationService.getUnreadCount()
      
      if (response.data && !response.data.error) {
        const unreadCount = response.data.obj.unread_count
        console.log('📈 Contador atualizado:', unreadCount, 'notificações não lidas')
        
        // Emite evento para atualizar o badge
        this.emitUnreadCountUpdate(unreadCount)
      }
    } catch (error) {
      console.error('❌ Erro ao buscar contador de notificações:', error)
    }
  }

  /**
   * Emite evento de atualização do contador
   * @param {number} count - Número de notificações não lidas
   */
  emitUnreadCountUpdate(count) {
    const unreadCountListeners = this.listeners.get('unreadCountUpdate') || []
    
    unreadCountListeners.forEach(callback => {
      try {
        callback(count)
      } catch (error) {
        console.error('❌ Erro ao processar atualização de contador:', error)
      }
    })
  }

  /**
   * Exibe notificação nativa do navegador
   * @param {Object} notification - Dados da notificação
   */
  showBrowserNotification(notification) {
    // Verifica se o navegador suporta notificações
    if (!('Notification' in window)) {
      console.warn('⚠️ Este navegador não suporta notificações')
      return
    }

    // Solicita permissão se necessário
    if (Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.createBrowserNotification(notification)
        }
      })
    } else if (Notification.permission === 'granted') {
      this.createBrowserNotification(notification)
    }
  }

  /**
   * Cria a notificação nativa do navegador
   * @param {Object} notification - Dados da notificação
   */
  createBrowserNotification(notification) {
    try {
      const { title, message, type } = notification
      
      // Ícone baseado no tipo
      const getIcon = (notificationType) => {
        switch (notificationType) {
          case 'success':
            return '✅'
          case 'warning':
            return '⚠️'
          case 'error':
            return '❌'
          case 'info':
          default:
            return '🔔'
        }
      }

      const browserNotification = new Notification(`${getIcon(type)} ${title}`, {
        body: message,
        icon: '/favicon.ico', // Usa o favicon do projeto
        badge: '/favicon.ico',
        tag: `bds-notification-${Date.now()}`, // Tag única para evitar duplicatas
        requireInteraction: false, // Não requer interação para fechar
        silent: false // Permite som
      })

      // Auto-fecha após 5 segundos
      setTimeout(() => {
        browserNotification.close()
      }, 5000)

      // Opcional: ação ao clicar na notificação
      browserNotification.onclick = () => {
        window.focus() // Foca na janela do navegador
        browserNotification.close()
      }

    } catch (error) {
      console.error('❌ Erro ao criar notificação do navegador:', error)
    }
  }

  /**
   * Registra um listener para um evento específico
   * @param {string} event - Nome do evento
   * @param {Function} callback - Função callback
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, [])
    }
    
    this.listeners.get(event).push(callback)
    // Para o evento 'notification', não registramos no socket pois já é tratado internamente
    // Para outros eventos, registra também no socket
    if (this.socket && event !== 'notification') {
      this.socket.on(event, callback)
    }
  }

  /**
   * Remove um listener de um evento
   * @param {string} event - Nome do evento
   * @param {Function} callback - Função callback a ser removida
   */
  off(event, callback) {
    const eventListeners = this.listeners.get(event)
    
    if (eventListeners) {
      const index = eventListeners.indexOf(callback)
      if (index > -1) {
        eventListeners.splice(index, 1)
      }
    }
    
    // Remove também do socket se existir
    if (this.socket && event !== 'notification') {
      this.socket.off(event, callback)
    }
  }

  /**
   * Trata a reconexão automática
   */
  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      
      console.log(`🔄 Tentativa de reconexão ${this.reconnectAttempts}/${this.maxReconnectAttempts}`)
      
      setTimeout(() => {
        if (this.socket) {
          const accessToken = this.getAccessToken()
          if (!accessToken) {
            console.warn('⚠️ Reconexão WebSocket cancelada: token JWT ausente')
            return
          }
          this.socket.auth = { token: accessToken }
          this.socket.connect()
        }
      }, this.reconnectDelay * this.reconnectAttempts)
    } else {
      console.error('❌ Máximo de tentativas de reconexão atingido')
    }
  }

  /**
   * Desconecta do WebSocket
   */
  disconnect() {
    if (this.socket) {
      console.log('🔌 Desconectando WebSocket...')
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
      this.reconnectAttempts = 0
    }
  }

  /**
   * Verifica se está conectado
   * @returns {boolean}
   */
  isSocketConnected() {
    return this.isConnected && this.socket && this.socket.connected
  }

  /**
   * Emite um evento para o servidor
   * @param {string} event - Nome do evento
   * @param {Object} data - Dados a serem enviados
   */
  emit(event, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data)
    } else {
      console.warn('⚠️ WebSocket não conectado. Não foi possível emitir evento:', event)
    }
  }
}

// Instância singleton do serviço
const websocketService = new WebSocketService()

export default websocketService
